import React from 'react';

import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { BLOCK_CONTROLS_RIGHT_GROUP } from '@atlaskit/editor-common/block-controls/surface-keys';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { RegisterButton } from '@atlaskit/editor-ui-control-model/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AiSuggestionsPlugin } from './aiSuggestionsPluginType';
import { acceptSuggestionAction } from './editor-actions/accept-suggestion';
import { discardAllSuggestionsAction } from './editor-actions/discard-all-suggestions';
import { discardSuggestionAction } from './editor-actions/discard-suggestion';
import { hideSuggestionDiffAction } from './editor-actions/hide-suggestion-diff';
import { refreshSuggestionsAction } from './editor-actions/refresh-suggestions';
import { selectSuggestionAction } from './editor-actions/select-suggestion';
import { setReviewingAgentAction } from './editor-actions/set-reviewing-agent';
import { setUIVisibilityAction } from './editor-actions/set-ui-visibility';
import { showSuggestionDiffAction } from './editor-actions/show-suggestion-diff';
import { submitSuggestionFeedbackAction } from './editor-actions/submit-suggestion-feedback';
import { setThinkingStateAction } from './editor-actions/thinking';
import { createAcceptShimmerPlugin, getAcceptShimmerDuration } from './pm-plugins/acceptShimmer';
import { aiSuggestionsPluginKey, createPlugin } from './pm-plugins/main';
import { createThinkingPlugin, thinkingPluginKey } from './pm-plugins/thinking-plugin';
import { AcceptShimmerGlobalStyles } from './ui/components/AcceptShimmerGlobalStyles';
import { FollowUpCardContentComponent } from './ui/components/FollowUpCard/FollowUpCardContentComponent';
import { HoverHighlightGlobalStyles } from './ui/components/HoverHighlightGlobalStyles';
import { SuggestionCardContentComponent } from './ui/components/SuggestionCard/SuggestionCardContentComponent';
import { SuggestionRightSurfaceButton } from './ui/components/SuggestionRightSurfaceButton';
import { ThinkingContentComponent } from './ui/components/Thinking/ThinkingContentComponent';
import { AISuggestionsToolbarComponents } from './ui/toolbar/toolbar';

export const aiSuggestionsPlugin: AiSuggestionsPlugin = ({ api, config }) => {
	const { contentAri, onShowSidebar, ConversationAssistant, onSubmitSuggestionFeedback } = config;

	if (api?.toolbar) {
		api.toolbar.actions.registerComponents(AISuggestionsToolbarComponents({ api }));
	}

	// EDITOR-8771: prefer the right surface via the UI Control Registry API once the migration
	// gate is on, falling back to the legacy widget decoration built in `calculateSuggestionsDecorations`
	// (see `main.tsx`) when it is off.
	if (isExperimentEnabled('platform_editor_block_control_migration')) {
		const suggestionRightSurfaceButton: RegisterButton = {
			component: ({ surfaceContext }) => (
				<SuggestionRightSurfaceButton surfaceContext={surfaceContext} api={api} />
			),
			isHidden: ({ surfaceContext } = {}) => {
				if (api?.editorViewMode?.sharedState.currentState()?.mode === 'view') {
					return true;
				}
				const aiSuggestionsState = api?.aiSuggestions?.sharedState.currentState();
				// `setUIVisibility(false)` hides every suggestion block control, not just the cards.
				if (aiSuggestionsState?.isHidden) {
					return true;
				}
				const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
				const localId = context?.targetNode.node.attrs?.localId as string | undefined;
				if (!localId) {
					return true;
				}
				const suggestionIds = aiSuggestionsState?.suggestionIdsByBaseLocalId.get(localId);
				return !suggestionIds || suggestionIds.size === 0;
			},
			key: 'ai-suggestions-right-surface-button',
			parents: [{ ...BLOCK_CONTROLS_RIGHT_GROUP, rank: 100 }],
			type: 'button',
		};
		api?.uiControlRegistry?.actions.register([suggestionRightSurfaceButton]);
	}

	return {
		name: 'aiSuggestions',
		actions: {
			acceptSuggestion: acceptSuggestionAction(api),
			discardSuggestion: discardSuggestionAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			showSuggestionDiff: showSuggestionDiffAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			hideSuggestionDiff: hideSuggestionDiffAction(api),
			discardAllSuggestions: discardAllSuggestionsAction(api),
			refreshSuggestions: refreshSuggestionsAction(api, contentAri),
			selectSuggestion: selectSuggestionAction(api),
			setReviewingAgent: setReviewingAgentAction(api),
			setThinkingState: setThinkingStateAction(api),
			setUIVisibility: setUIVisibilityAction(api),
			submitSuggestionFeedback: submitSuggestionFeedbackAction(api, onSubmitSuggestionFeedback),
		},
		getSharedState(editorState: EditorState | undefined) {
			if (!editorState) {
				return undefined;
			}
			const pluginState = aiSuggestionsPluginKey.getState(editorState);
			if (!pluginState) {
				return undefined;
			}
			const thinkingState = thinkingPluginKey.getState(editorState);
			return {
				acceptShimmerDuration: getAcceptShimmerDuration(editorState),
				reviewResults: thinkingState?.reviewResults,
				isHidden: pluginState.isHidden,
				selectedSuggestion: pluginState.selectedSuggestion,
				suggestionById: pluginState.suggestionById,
				baseLocalIdBySuggestionId: pluginState.baseLocalIdBySuggestionId,
				suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
				orderedSuggestionIds: pluginState.orderedSuggestionIds,
				followUpCard: pluginState.followUpCard,
				regeneratingSuggestionIds: pluginState.regeneratingSuggestionIds,
				thinkingStatus: thinkingState?.status ?? 'idle',
				thinkingStartedAt: thinkingState?.startedAt ?? null,
				thinkingProgressTarget: thinkingState?.progressTarget ?? null,
				thinkingError: thinkingState?.error ?? null,
				reviewingAgent: thinkingState?.reviewingAgent ?? null,
			};
		},
		pmPlugins() {
			return [
				{
					name: 'aiSuggestionsPlugin',
					plugin: ({ getIntl, nodeViewPortalProviderAPI }) => {
						const intl = getIntl?.();
						return createPlugin({ api, intl, contentAri, nodeViewPortalProviderAPI });
					},
				},
				...(isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs')
					? [
							{
								name: 'aiSuggestionsAcceptShimmerPlugin',
								plugin: () => createAcceptShimmerPlugin(),
							},
						]
					: []),
				{
					name: 'aiSuggestionsThinkingPlugin',
					plugin: ({ dispatch }) => {
						return createThinkingPlugin({
							dispatch,
							getSuggestionIds: (state) => {
								const suggestions = aiSuggestionsPluginKey.getState(state);
								return suggestions?.contentAri !== undefined
									? suggestions.orderedSuggestionIds
									: undefined;
							},
						});
					},
				},
			];
		},
		contentComponent({ editorView }) {
			return (
				<>
					{isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs') && (
						<AcceptShimmerGlobalStyles />
					)}
					<HoverHighlightGlobalStyles />
					{
						/**
						 * Both need the ConversationAssistant store: the thinking surface to invoke the
						 * skill, track thinking states and abort requests, and the follow-up card to
						 * resolve the Content Reviewer agent for its avatar.
						 */
						ConversationAssistant && (
							<ConversationAssistant>
								<ThinkingContentComponent api={api} editorView={editorView} />
								<FollowUpCardContentComponent api={api} view={editorView} />
							</ConversationAssistant>
						)
					}
					<SuggestionCardContentComponent
						api={api}
						onShowSidebar={onShowSidebar}
						view={editorView}
					/>
				</>
			);
		},
	};
};
